/**
 * WorldEvent — browser mirror of tool-runtime/src/agent/worldEvents.ts
 *
 * Keep in sync with the tool-runtime version manually.
 * All clients (browser, tool-runtime, backend workers) emit this shape.
 */

export type EventActorKind = 'user' | 'agent' | 'system';

export interface EventActor {
  kind: EventActorKind;
  /** userId (auth0 subject), agentRunId (run_xxx_xxx), or 'system' */
  id: string;
}

export interface EventEntityRef {
  id: string;         // MongoDB _id
  llmId?: string;     // friendly LLM identifier
  type: string;       // entityType
  name: string;
}

export interface WorldEvent {
  id: string;              // 12-char random
  type: string;            // namespaced: 'integration.connected', 'entity.created', etc.
  ts: number;              // Date.now()
  actor: EventActor;
  spaceId?: string;        // space-scoped events
  dataspaceId?: string;    // always set
  entity?: EventEntityRef;
  data: Record<string, any>;
}

// ── Tier 1: Agent trigger events (deterministic reactions) ────────────────────
export const EVENT_TYPES = {
  // Integrations
  INTEGRATION_CONNECTED:        'integration.connected',
  INTEGRATION_DISCONNECTED:     'integration.disconnected',
  INTEGRATION_SYNC_COMPLETED:   'integration.sync_completed',
  INTEGRATION_SYNC_FAILED:      'integration.sync_failed',

  // Tasks & work
  TASK_COMPLETED:               'task.completed',
  TASK_CREATED:                 'task.created',
  PLAN_STEP_CHANGED:            'plan.step_changed',

  // Communication
  EMAIL_RECEIVED:               'email.received',
  CALENDAR_EVENT_STARTING_SOON: 'calendar.event.starting_soon',
  CALENDAR_EVENT_ENDED:         'calendar.event.ended',

  // Entity lifecycle (Tier 1)
  ENTITY_CREATED:               'entity.created',
  ENTITY_DELETED:               'entity.deleted',

  // Agent lifecycle
  AGENT_STARTED:                'agent.started',
  AGENT_COMPLETED:              'agent.completed',
  AGENT_TOOL_CALLED:            'agent.tool_called',

  // Skill / automation
  SKILL_FIRED:                  'skill.fired',

  // ── Tier 2: Awareness events ──────────────────────────────────────────────
  ENTITY_OPENED:                'entity.opened',
  ENTITY_CLOSED:                'entity.closed',
  ENTITY_EDITED:                'entity.edited',
  ENTITY_COMMENTED:             'entity.commented',
  ENTITY_LINKED:                'entity.linked',
  ENTITY_MOVED:                 'entity.moved',
  MESSAGE_SENT:                 'message.sent',
  SPACE_OPENED:                 'space.opened',

  // ── Tier 3: Attention / ML training data ─────────────────────────────────
  EDITOR_SELECTION:             'editor.selection',
  EDITOR_CURSOR_IDLE:           'editor.cursor_idle',
  WINDOW_FOCUSED:               'window.focused',
  SCROLL_MILESTONE:             'scroll.milestone',
  SEARCH_PERFORMED:             'search.performed',
  SEARCH_RESULT_OPENED:         'search.result_opened',
  COMMAND_EXECUTED:             'command.executed',
} as const;

export type EventType = typeof EVENT_TYPES[keyof typeof EVENT_TYPES];

/** Create a WorldEvent with auto-generated id and current timestamp */
export function makeEvent(
  type: EventType,
  actor: EventActor,
  data: Record<string, any> = {},
  refs: { spaceId?: string; dataspaceId?: string; entity?: EventEntityRef } = {},
): WorldEvent {
  return {
    id: Math.random().toString(36).slice(2, 14),
    type,
    ts: Date.now(),
    actor,
    ...refs,
    data,
  };
}

/**
 * Get the dataspaceId for the current user/org context.
 * For org spaces: the org's _id. For personal spaces: the auth0 user sub.
 */
export function getCurrentDataspaceId(): string {
  try {
    const { getCurrentDataspaceContext } = require('../stores/dataspaceContextStore');
    const context = getCurrentDataspaceContext();
    if (context.type === 'org' && context.orgId) {
      return context.orgId;
    }
  } catch {
    // context store not available
  }
  return localStorage.getItem('userId') || '';
}
